import { isString, isFunction, isArray, noop } from 'lodash-es';

import { isEnd } from './channel';
import { EFFECT } from './effect';
import { TERMINATE, TASK_CANCEL, SELF_CANCELLATION } from './innerActionSymbol';
import { taskProcessor } from './taskProcessor';
import { asap, immediately } from './taskSchedule';
import { isPromise, resolvePromise, isIterator, getMetaInfo } from './utils';

const matcher = (d) => {
	if (d === '*') {
		return () => true;
	}
	if (isFunction(d)) {
		return (input) => d(input);
	}
	if (isString(d)) {
		return (input) => input.type === String(d);
	}
	if (isArray(d)) {
		return (input) => d.indexOf(input.type) > -1;
	}
};
const runTakeEffect = (env, { channel = env.channel, pattern }, cb) => {
	// console.log({env, channel, pattern, cb});
	const takeCb = (input) => {
		if (input instanceof Error) {
			cb(input, true);
			return;
		}
		if (isEnd(input)) {
			cb(TERMINATE);
			return;
		}
		cb(input);
	};

	try {
		channel.take(takeCb, pattern !== undefined ? matcher(pattern) : null);
	} catch (error) {
		cb(error, true);
		return;
	}
	cb.cancel = takeCb.cancel;
};

const runPutEffect = (env, { action, resolve }, cb) => {
	// 增加到待执行队列
	asap(() => {
		let result;
		try {
			result = env.dispatch(action);
		} catch (error) {
			cb(error, true);
			return;
		}
		if (resolve && isPromise(result)) {
			resolvePromise(result, cb);
		} else {
			cb(result);
		}
	});
};

const runCallEffect = (env, { context, fn, args }, cb) => {
	try {
		let result = null;
		if (context) {
			result = fn.apply(context, args);
		} else {
			result = fn(...args);
		}
		if (isPromise(result)) {
			resolvePromise(result, cb);
			return;
		}
		if (isIterator(result)) {
			taskProcessor(env, result, getMetaInfo(fn), false, cb);
			return;
		}
		cb(result);
	} catch (error) {
		cb(error, true);
	}
};

function getIteratorMetaInfo(iterator, fn) {
	if (iterator && iterator.isSagaIterator) {
		return { name: iterator.meta.name };
	}
	return getMetaInfo(fn);
}
const makeIterator = (next) => {
	const iterator = {
		meta: { name: 'iterator' },
		next,
		throw: (err) => {
			throw err;
		},
		return: (value) => ({ value, done: true }),
		isSagaIterator: true,
	};
	if (typeof Symbol !== 'undefined') {
		iterator[Symbol.iterator] = () => iterator;
	}
	return iterator;
};
function createTaskIterator({ context, fn, args }) {
	try {
		const result = fn.apply(context, args);
		if (isIterator(result)) {
			return result;
		}
		let resolved = false;
		const next = (arg) => {
			if (!resolved) {
				resolved = true;
				return { value: result, done: !isPromise(result) };
			} else {
				return { value: arg, done: true };
			}
		};
		return makeIterator(next);
	} catch (err) {
		return makeIterator(() => {
			throw err;
		});
	}
}
const runForkEffect = (env, { context, fn, args, detached }, cb, { task: parent }) => {
	const taskIterator = createTaskIterator({ context, fn, args });
	const meta = getIteratorMetaInfo(taskIterator, fn);

	immediately(() => {
		const child = taskProcessor(env, taskIterator, meta, detached, undefined);
		// console.log(child, detached, child.isAborted());
		if (detached) {
			cb(child);
		} else {
			if (child.isRunning()) {
				parent.queue.addTask(child);
				cb(child);
			} else if (child.isAborted()) {
				parent.queue.abort(child.error());
			} else {
				cb(child);
			}
		}
	});
};

const runGetEffect = (env, { selector, args }, cb) => {
	try {
		const state = selector(env.getState(), ...args);
		cb(state);
	} catch (error) {
		cb(error, true);
	}
};

function cancelSingleTask(taskToCancel) {
	if (taskToCancel.isRunning()) {
		taskToCancel.cancel();
	}
}

const runCancelEffect = (env, taskOrTasks, cb, { task }) => {
	if (taskOrTasks === SELF_CANCELLATION) {
		cancelSingleTask(task);
	} else if (isArray(taskOrTasks)) {
		taskOrTasks.forEach(cancelSingleTask);
	} else {
		cancelSingleTask(taskOrTasks);
	}
	cb();
};
const runCancelledEffect = (env, data, cb, { task }) => {
	cb(task.isCancelled());
};

const createAllChildCallback = (list, callback) => {
	const keys = Object.keys(list);
	const totalCount = keys.length;
	let completedCount = 0;
	let completed;
	let _res = isArray(list) ? [] : {};
	let _callbacks = {};
	const checkListFinished = () => {
		if (totalCount === completedCount) {
			completed = true;
			callback(_res);
		}
	};
	keys.forEach((k) => {
		const _callback = (res, isErr) => {
			if (completed) {
				return;
			}
			if (isErr || res === TERMINATE || res === TASK_CANCEL) {
				callback.cancel && callback.cancel();
				callback(res, isErr);
			} else {
				_res[k] = res;
				completedCount++;
				checkListFinished();
			}
		};
		_callback.cancel = () => {
			if (!completed) {
				completed = true;
				keys.forEach((k) => _callbacks[k].cancel());
			}
		};

		_callbacks[k] = _callback;
	});

	return _callbacks;
};
const runAllEffect = (env, effects, cb, { digestEffect }) => {
	const keys = Object.keys(effects);
	if (keys.length === 0) {
		cb(isArray(effects) ? [] : {});
		return;
	}
	const callbacks = createAllChildCallback(effects, cb);
	keys.forEach((k) => {
		digestEffect(effects[k], callbacks[k]);
	});
};
// const runRaceEffect = ()=>{};

export default {
	[EFFECT.TAKE]: runTakeEffect,
	[EFFECT.PUT]: runPutEffect,
	[EFFECT.CALL]: runCallEffect,
	[EFFECT.FORK]: runForkEffect,
	[EFFECT.SELECT]: runGetEffect,
	[EFFECT.CANCEL]: runCancelEffect,
	[EFFECT.CANCELLED]: runCancelledEffect,
	[EFFECT.ALL]: runAllEffect,
	// [EFFECT.RACE]: runRaceEffect,
};
